import React from 'react';
import {t} from 'i18next';
import {LinkProps} from 'react-router-dom';
import {PATHS} from '../../Routes';
import {useActions, useErrorModal, useGuestGroup, usePath} from 'hooks';
import {useDeleteGuest} from '../../hooks/useDeleteGuest';
import {GUEST_STATUSES, ShortGuest} from '@guestapp/sdk';
import deleteGuestIcon from '../../assets/icons/delete-guest-icon.svg';
import incompleteIcon from '../../assets/icons/incomplete-icon.svg';
import completeIcon from '../../assets/icons/complete-icon.svg';
import IconButton from '../common/IconButton';
import CustomIcon from '../common/CustomIcon';
import {
  DeleteGuestIcon,
  GuestContainer,
  Item,
  LinkItem,
  NavigationStatusTextProps,
  StatusIcon,
  StatusText,
  Texts,
  Title,
} from './styled';

type GuestPanelLinkItem = LinkProps;
type GuestPanelDivItem = React.HTMLAttributes<HTMLDivElement>;

function isLink(arg: GuestPanelLinkItem | GuestPanelDivItem): arg is GuestPanelLinkItem {
  return Boolean((arg as GuestPanelLinkItem).to);
}

type GuestPanelItemProps = {children: React.ReactNode} & NavigationStatusTextProps &
  (GuestPanelLinkItem | GuestPanelDivItem);
const GuestPanelItem = React.memo((props: GuestPanelItemProps) => {
  if (isLink(props)) {
    return <LinkItem {...props} />;
  } else {
    return <Item {...props} />;
  }
});

type GuestStatusesTexts = {
  [key in GUEST_STATUSES]?: string;
};

const guestStatusText: GuestStatusesTexts = {
  [GUEST_STATUSES.complete]: 'completed',
  [GUEST_STATUSES.verification_pending]: 'verification_pending',
  [GUEST_STATUSES.incomplete]: 'incomplete',
};

function GuestsPanel() {
  const {guestGroup} = useGuestGroup();
  const {getMainPath} = usePath();
  const {ErrorModal, displayError} = useErrorModal();
  const {resetAllIVState} = useActions();
  const members = guestGroup?.members;
  const enableGuestsRemoval = window?.ChekinProSettings?.enableGuestsRemoval;
  const {mutate: deleteGuestMutate, isLoading: isDeleting} = useDeleteGuest({
    onError: displayError,
  });

  const getLinkToUpdateGuestProcessView = React.useCallback(
    (guest?: ShortGuest) => {
      const guestId = guest?.id;
      const guestStatus = guest?.guestapp_status;
      const isVerificationPending = guestStatus === GUEST_STATUSES.verification_pending;
      const isPreRegisteredAndInCompleted =
        guest?.is_pre_registered && guestStatus === GUEST_STATUSES.incomplete;
      if (guestId) {
        if (isVerificationPending) {
          return getMainPath(`${PATHS.verification.main}/${guestId}`);
        }
        if (isPreRegisteredAndInCompleted) {
          return getMainPath(`${PATHS.addPersonalDataForm}/${guestId}`);
        }
      }
    },
    [getMainPath],
  );

  return (
    <GuestContainer $loading={isDeleting} className="guests-panel">
      {members
        ? members.map((guestMember, index: number) => {
            const {full_name, guestapp_status} = guestMember;
            const completed = guestapp_status === GUEST_STATUSES.complete;
            const statusIcon = completed ? completeIcon : incompleteIcon;
            const statusText = guestStatusText[guestapp_status] || '';
            const isEditable =
              (guestMember.is_pre_registered &&
                guestMember.guestapp_status === GUEST_STATUSES.incomplete) ||
              guestMember.guestapp_status === GUEST_STATUSES.verification_pending;
            const link = isEditable
              ? getLinkToUpdateGuestProcessView(guestMember)
              : undefined;
            const handleClick = () => isEditable && resetAllIVState();
            const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.preventDefault();
              if (enableGuestsRemoval && guestMember.id) {
                deleteGuestMutate(guestMember.id);
              }
            };

            return (
              <GuestPanelItem
                key={index}
                $completed={completed}
                to={link}
                onClick={handleClick}
                className="guests-panel__item"
              >
                <Texts>
                  <Title className="guests-panel__title">{full_name}</Title>
                  <StatusText className="guests-panel__status" $completed={completed}>
                    <StatusIcon src={statusIcon} />
                    {t(statusText)}
                  </StatusText>
                </Texts>
                {enableGuestsRemoval && (
                  <DeleteGuestIcon
                    className="guests-panel__delete-btn"
                    iconSrc={deleteGuestIcon}
                    onClick={handleDelete}
                  />
                )}
                {isEditable && (
                  <IconButton
                    className="guests-panel__edit-btn"
                    icon={<CustomIcon size={24} name={'grayRightArrow'} />}
                  />
                )}
              </GuestPanelItem>
            );
          })
        : null}
      <ErrorModal />
    </GuestContainer>
  );
}

export {GuestsPanel};
